import type { FC } from 'react'
import { useCallback } from 'react'
import type { StackProps } from '@chakra-ui/react'
import { VStack } from '@chakra-ui/react'
import type { AddToCartPayload, ICartItemDetailSubItem } from 'ecosystem'
import ProductTitle from '../../ProductTitle'
import BrandTitle from '../../BrandTitle'
import ProductDetailPrice from '../../ProductDetailPrice'
import { useProductContext } from '../../../hooks/useProductContext'
import VariantPicker from '../../VariantPicker/VariantPicker'
import ProductDetailAddToCart from '../../ProductDetailAddToCart'
import useConfigurationVariants from './useConfigurationVariants'

type ConfigurationProductInformationProps = StackProps

const ConfigurationProductInformation: FC<ConfigurationProductInformationProps> = ({
  ...props
}) => {
  const { product, onAddToCart, variants: variantsByIds } = useProductContext()
  const {
    isConfigurationBuyable,
    isConfigurationSelected,
    selectConfiguration,
    prices,
    selectConfigurationOnUpdate,
    selectedConfigurations
  } = useConfigurationVariants(product, variantsByIds)

  const addConfigurationToCart = useCallback(
    ({ product: p, qty }: AddToCartPayload) => {
      const subItems: ICartItemDetailSubItem[] = selectedConfigurations.map((variant) => ({
        product: variant,
        originalQty: qty,
        qty
      }))

      onAddToCart({
        id: p.id,
        type: 'SINGLE',
        qty,
        meta: {
          singleType: {
            product: {
              ...p,
              ...prices
            },
            subItems
          }
        }
      })
    },
    [onAddToCart, prices, selectedConfigurations]
  )

  const configurations = product.configurations ? [...product.configurations] : []

  return (
    <VStack spacing={3} {...props}>
      <VStack spacing={0} w="100%">
        {product.brand?.name ? <BrandTitle title={product.brand.name} /> : null}
        <ProductTitle flex={1} title={product.name} />
      </VStack>

      <ProductDetailPrice {...prices} />

      <VStack pb={6} w="100%">
        {configurations
          .sort((a, b) => a.position - b.position)
          .map((conf, i) => {
            return (
              <VariantPicker
                borderColor="gray.100"
                borderRadius="sm"
                borderWidth={2}
                isCheckingInStock
                isOptional={conf.optional}
                isVariantSelected={isConfigurationSelected}
                // eslint-disable-next-line react/no-array-index-key -- No ID available
                key={i}
                label={conf.label}
                onSelectVariant={selectConfiguration}
                onVariantsUpdate={selectConfigurationOnUpdate}
                p={2}
                variantGroupId={conf.variantGroupId}
              />
            )
          })}
      </VStack>

      <ProductDetailAddToCart
        key={product.id}
        isDisabled={!isConfigurationBuyable}
        onAddToCart={addConfigurationToCart}
        totalPrices={prices}
      />
    </VStack>
  )
}

export default ConfigurationProductInformation
